import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_API_URL;

// List consultants with filters + pagination
// Expected response shape: { items, page, pageSize, totalItems, totalPages }
export const listConsultants = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/consultants`, {
    params,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Get single consultant
export const getConsultant = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/consultants/${consultantId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Download a consultant's stored CV file as a blob.
// Caller is responsible for reading `.data` (Blob) and `.headers` (e.g.
// content-disposition for the filename).
export const downloadConsultantCv = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/consultants/${consultantId}/cv`, {
    responseType: "blob",
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response;
};

// Create consultant manually
export const createConsultant = async (payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/consultants`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Update consultant
export const updateConsultant = async (consultantId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(`${BASE_URL}/consultants/${consultantId}`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Delete consultant
export const deleteConsultant = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(`${BASE_URL}/consultants/${consultantId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const confirmDeleteConsultant = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/consultants/${consultantId}/confirm-delete`, null, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Create consultant from CV (AI extraction)
export const createConsultantFromCv = async (file, language, options = {}) => {
  const { buId } = options || {};
  const formData = new FormData();
  formData.append("cvFile", file);
  if (language) {
    formData.append("language", language);
  }
  const response = await axios.post(`${BASE_URL}/consultants/from-cv`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(buId ? { "X-Business-Unit-Id": buId } : {})
    }
  });
  return response.data;
};

// Import consultants from CSV / Excel file
// Backend expects: POST /consultants/import with "file" (CSV or .xlsx)
// Returns a ConsultantListResponse: { items, page, pageSize, totalItems, totalPages }
export const importConsultantsFromFile = async (file, options = {}) => {
  const { buId } = options || {};
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post(`${BASE_URL}/consultants/import`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(buId ? { "X-Business-Unit-Id": buId } : {})
    }
  });

  return response.data;
};

// Download the 2-tab .xlsx template (Summary + Consultant) for the Consultants import.
export const getConsultantsImportTemplate = async (options = {}) => {
  const { buId, lang } = options || {};
  const response = await axios.get(`${BASE_URL}/consultants/import/template`, {
    responseType: "blob",
    params: lang ? { lang } : undefined,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response;
};

// Bulk upload CVs to create consultants
export const bulkUploadCvs = async (files, language = "français", options = {}) => {
  const { buId } = options || {};
  const formData = new FormData();
  files.forEach((file) => formData.append("cvFiles", file));
  formData.append("language", language);
  const response = await axios.post(`${BASE_URL}/consultants/bulk-upload-cv`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(buId ? { "X-Business-Unit-Id": buId } : {})
    }
  });
  return response.data;
};

// Add a comment to a consultant
export const addConsultantComment = async (
  consultantId,
  comment,
  options = {},
  parentCommentId
) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/comments`,
    { comment, ...(parentCommentId ? { parentCommentId } : {}) },
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Get comments for a consultant (thread tree)
export const getConsultantComments = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/comments`,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Get consultant month-by-month timeline (assignments, availability, key dates)
// GET /consultants/{consultant_id}/timeline?fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD
// Response shape (backend): { rangeStart, rangeEnd, consultant, months: [...] }
export const getConsultantTimeline = async (
  consultantId,
  params = {},
  options = {}
) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/timeline`,
    {
      params,
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

export const inviteConsultantPortalAccess = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/portal-access/invite`,
    null,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const resendConsultantPortalAccess = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/portal-access/resend`,
    null,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const disableConsultantPortalAccess = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(
    `${BASE_URL}/consultants/${consultantId}/portal-access`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const listConsultantInternalActivityReports = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/activity-reports`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const approveConsultantActivityReport = async (reportId, payload = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/consultant-activity-reports/${reportId}/approve`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const rejectConsultantActivityReport = async (reportId, payload = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/consultant-activity-reports/${reportId}/reject`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const listConsultantInternalLeaveRequests = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/leave-requests`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const approveConsultantLeaveRequest = async (requestId, payload = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/consultant-leave-requests/${requestId}/approve`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const rejectConsultantLeaveRequest = async (requestId, payload = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/consultant-leave-requests/${requestId}/reject`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const listConsultantInternalAssignmentFeedback = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/assignment-feedback`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

// Conversations / Interactions

export const getConsultantConversations = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/conversations`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const createConsultantConversation = async (consultantId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/conversations`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const updateConsultantConversation = async (consultantId, conversationId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(
    `${BASE_URL}/consultants/${consultantId}/conversations/${conversationId}`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const deleteConsultantConversation = async (consultantId, conversationId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(
    `${BASE_URL}/consultants/${consultantId}/conversations/${conversationId}`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

// Assignments

export const listConsultantAssignments = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/assignments`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

// Skills summary

export const getConsultantSkillsSummary = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/skills-summary`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

// Suggest consultant to sales rep (reverse matching)

export const suggestConsultantToSales = async (consultantId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/suggest-to-sales`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

// Monthly ratings

export const listMonthlyRatings = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/monthly-ratings`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const createMonthlyRating = async (consultantId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/monthly-ratings`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const updateMonthlyRating = async (consultantId, ratingId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(
    `${BASE_URL}/consultants/${consultantId}/monthly-ratings/${ratingId}`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const deleteMonthlyRating = async (consultantId, ratingId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(
    `${BASE_URL}/consultants/${consultantId}/monthly-ratings/${ratingId}`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

// Annual reviews

export const listAnnualReviews = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/consultants/${consultantId}/annual-reviews`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const createAnnualReview = async (consultantId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/consultants/${consultantId}/annual-reviews`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const updateAnnualReview = async (consultantId, reviewId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(
    `${BASE_URL}/consultants/${consultantId}/annual-reviews/${reviewId}`,
    payload,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};

export const deleteAnnualReview = async (consultantId, reviewId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(
    `${BASE_URL}/consultants/${consultantId}/annual-reviews/${reviewId}`,
    buId ? { headers: { "X-Business-Unit-Id": buId } } : {}
  );
  return response.data;
};
